import React, { useCallback, useMemo } from 'react';
import OpenGraph from '@/components/OpenGraph';
import { Flex, useDisclosure } from '@chakra-ui/react';
import FullPage from '@/src/components/UI/FullPage';
import FunwooAPI, { SeminarConsultationFeedbackDto, SurveyDto } from '@/swagger';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import clsx from 'clsx';
import {
  FieldBlock,
  FieldRow,
  Form,
  FormHeading,
  FormSectionTitle,
  SubmitButton,
} from '@/src/components/feature/ConsultantForm/ConsultantFormV2';
import classNames from 'classnames';
import { FormProvider, useController, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import {
  Agreement,
  budgetOptions,
  dDayOptions,
  EmailField,
  floorPlanOptions,
  FunwooRadio,
  Input,
  interestingCityOptions,
  Label,
  NameField,
  PhoneField,
  PopupSelect,
} from '@/src/components/feature/ConsultantForm/ConsultantForm';
import CheckIcon from '@/src/components/feature/CheckIcon';
import Flexbox from '@/components/common/Flexbox';
import { HookFormError } from '@/src/components/common/FieldError';
import { GreetingPopup } from '@/src/components/feature/ConsultantForm/GreetingPopup';
import { useReCaptcha } from '@/lib/next-recaptcha-v3';
import { isNotEmptyString, isSet } from '@/utils/formatChecker';

interface SurveyDtoFormValue extends Omit<SurveyDto, 'answer'> {
  answer: Array<string>;
  options: Array<string>;
  isMultiple?: boolean;
  allowOther?: boolean;
  other?: string;
}

const getDefaultSurveyQuestions = (): Array<SurveyDtoFormValue> => [
  {
    question: '您對我們的整體服務印象為何？（可複選）',
    answer: [],
    isMultiple: true,
    options: ['專業細心服務', '回覆迅速效率', '物件符合需求', '房市資訊完整'],
  },
  {
    question: '以下哪些原因影響您暫時未進一步購屋？（可複選）',
    answer: [],
    isMultiple: true,
    options: [
      '重新評估資金配置',
      '專注其他投資計畫',
      '想多觀察市場變化',
      '親自赴美後再決定',
      '期待更多房型選擇',
      '期待更多區域選擇',
      '期待了解更多物件',
      '暫無急迫購房需求',
    ],
    allowOther: true,
  },
  {
    question: '以下哪些內容是您有興趣了解的？（可複選）',
    answer: [],
    isMultiple: true,
    options: ['最新的房市報告', '全新建案的資訊', '旅美看房團資訊', '一對一置產諮詢', '美國教育與留學'],
    allowOther: true,
  },
  {
    question: '未來若有說明會或活動，您希望我們通知您嗎？',
    answer: [],
    isMultiple: false,
    options: ['是，願意收到活動邀請', '否，暫時不需要'],
  },
];

interface FormValues extends Omit<SeminarConsultationFeedbackDto, 'surveys'> {
  agree: boolean;
  surveys: Array<SurveyDtoFormValue>;
}

const TransactionHoldFeedback = () => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { executeRecaptcha } = useReCaptcha();

  const methods = useForm<FormValues>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      phoneCountryCode: '886',
      phone: '',
      email: '',
      surveys: getDefaultSurveyQuestions(),
      budgetCurrency: 'USD',
      budget: '',
      dDay: '',
    },
  });

  const {
    control,
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = methods;

  const { fields } = useFieldArray({ control, name: 'surveys' });

  const onSubmit = useCallback(
    handleSubmit(async ({ agree: _, surveys, ...formValues }) => {
      const token = await executeRecaptcha('personal_consultation_v2');

      await FunwooAPI.mailApi.sendFeedbackSeminarConsultation(
        {
          ...formValues,
          surveys: surveys.map(({ question, answer, other = '' }) => ({
            question: question,
            answer: answer.concat(other).filter(isNotEmptyString).join(', '),
          })),
        },
        {
          headers: {
            Recaptcha: token,
          },
        }
      );
      reset();
      onOpen();
    }, console.warn),
    [handleSubmit]
  );

  return (
    // @ts-ignore
    <FormProvider {...methods}>
      <GreetingPopup isOpen={isOpen} onClose={onClose} />
      <OpenGraph
        title='FUNWOO 德載國際不動產 - 諮詢體驗滿意度'
        type='funwoo.seminar-consultation-feedback'
        url='https://funwoo.com.tw/seminar-consultation-feedback'
        keywords={['房產顧問', '房仲', 'FUNWOO 房產顧問']}
      />
      <FullPage
        hasHeaderPlaceHolder
        title={'諮詢體驗滿意度'}
        hasPadding={false}
        hasBottomMargin={false}
        frameProps={{
          maxW: 'auto',
          className: 'bg-black',
        }}
      >
        <Flex w={'100%'} minHeight={'60vh'} direction='column' maxW={1440} mx={'auto'}>
          <section data-title={'諮詢體驗滿意度'} id={'contact-us'} style={{ scrollMarginTop: '66px' }}>
            <div className={clsx('bg-black', 'md:mx-auto', 'px-6 sm:px-16 sm:pt-4 pb-8', 'text-white')}>
              <FormHeading>諮詢體驗滿意度</FormHeading>
              <Form onSubmit={onSubmit}>
                <p className={classNames('py-2', 'sm:text-center', 'whitespace-pre-wrap')}>
                  感謝您選擇 FUNWOO 美國不動產，讓我們有機會陪伴您開啟美國置產旅程。
                  <br />
                  我們重視每位客戶的想法與體驗，您的意見將幫助我們優化未來的服務品質。
                </p>
                <div className={classNames('pt-2', 'space-y-2')}>
                  <div className={classNames('space-y-2')}>
                    <FormSectionTitle>聯絡方式</FormSectionTitle>
                    <FieldRow>
                      <FieldBlock>
                        <NameField darkMode labelRight={'（ 請填寫出席者的中文全名 ）'} />
                      </FieldBlock>
                    </FieldRow>
                    <FieldRow>
                      <FieldBlock>
                        <PhoneField darkMode />
                      </FieldBlock>
                      <FieldBlock>
                        <EmailField darkMode required />
                      </FieldBlock>
                    </FieldRow>
                  </div>
                  <div className={classNames('space-y-2')}>
                    {fields.map((field, index) => (
                      <SurveyField key={field.id} {...field} index={index} />
                    ))}
                  </div>
                  <div className={classNames('space-y-2')}>
                    <FormSectionTitle>您的置產需求是否有發生改變？我們很樂意再次為您規劃</FormSectionTitle>
                    <FieldRow>
                      <FieldBlock>
                        <Label darkMode required={false}>
                          預算（美金）
                        </Label>
                        <FunwooRadio control={control} name={'budget'} options={budgetOptions} colorScheme={'white'} />
                        <HookFormError errors={errors} name={'budget'} />
                      </FieldBlock>
                      <FieldBlock>
                        <Label darkMode required={false}>
                          計劃購買時程
                        </Label>
                        <FunwooRadio control={control} name={'dDay'} options={dDayOptions} colorScheme={'white'} />
                        <HookFormError errors={errors} name={'dDay'} />
                      </FieldBlock>
                    </FieldRow>
                    <FieldRow>
                      <FieldBlock>
                        <Label darkMode required={false}>
                          有興趣的美國城市
                        </Label>
                        <PopupSelect
                          label={'置產城市'}
                          confirmWording={'選擇城市'}
                          control={control}
                          name={'interestedCity'}
                          options={interestingCityOptions}
                          isMultiple
                        />
                        <HookFormError errors={errors} name={'interestedCity'} />
                      </FieldBlock>
                      <FieldBlock>
                        <Label darkMode required={false}>
                          房型需求
                        </Label>
                        <PopupSelect
                          label={'房型需求'}
                          confirmWording={'選擇房型'}
                          control={control}
                          name={'interestedFloorPlan'}
                          options={floorPlanOptions}
                          isMultiple
                        />
                        <HookFormError errors={errors} name={'interestedFloorPlan'} />
                      </FieldBlock>
                    </FieldRow>
                    <FieldRow>
                      <FieldBlock>
                        <Label darkMode required={false}>
                          您是否有其他置產需求或想法需要我們知道？
                        </Label>
                        <Input darkMode {...register('note')} />
                      </FieldBlock>
                    </FieldRow>
                  </div>
                  <div className={classNames('sm:pt-4')}>
                    <Agreement darkMode startLeft align={'start'} className={clsx('sm:justify-center')} />
                    <div className={'py-3'}>
                      <SubmitButton type={'submit'} isLoading={isSubmitting}>
                        送出資料
                      </SubmitButton>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </section>
        </Flex>
      </FullPage>
    </FormProvider>
  );
};

export default TransactionHoldFeedback;

export const getStaticProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

const SurveyField: React.FC<SurveyDtoFormValue & { index: number }> = ({
  question,
  index,
  options,
  isMultiple,
  allowOther = false,
}) => {
  const { isOpen: otherSelected, onToggle: onOtherOptionToggle } = useDisclosure();

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();
  const {
    field: { value = [], onChange },
  } = useController({ control, name: `surveys.${index}.answer`, rules: { required: true } });

  const isOtherInputError = useMemo(() => {
    return isSet(errors?.surveys?.[index]?.other);
  }, [errors.surveys, index]);

  const onItemClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      const { optionValue } = event.currentTarget.dataset;

      if (!optionValue) throw new Error('optionValue is required');

      if (isMultiple) {
        if (value.includes(optionValue)) {
          onChange(value.filter((v: string) => v !== optionValue));
        } else {
          onChange(value.concat(optionValue));
        }
      } else {
        onChange([optionValue]);
      }
    },
    [value, onChange]
  );

  return (
    <FieldBlock>
      <FormSectionTitle>{question}</FormSectionTitle>
      <div className={clsx('space-y-2')}>
        <div
          className={clsx(
            'gap-x-2 gap-y-0',
            'grid sm:gap-x-4 grid-flow-dense sm:gap-y-2',
            '[grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]'
          )}
        >
          {options.map((option) => (
            <Flexbox
              as={'button'}
              // @ts-ignore
              type={'button'}
              align={'center'}
              justify={'start'}
              key={option}
              data-option-value={option}
              onClick={onItemClick}
              className={clsx('py-2', 'space-x-1')}
            >
              <CheckIcon checked={value.includes(option)} darkMode variant={isMultiple ? 'square' : 'circle'} />
              <p>{option}</p>
            </Flexbox>
          ))}
          {allowOther && (
            <Flexbox
              as={'button'}
              // @ts-ignore
              type={'button'}
              align={'center'}
              justify={'start'}
              onClick={onOtherOptionToggle}
              className={clsx('py-2', 'space-x-1')}
            >
              <CheckIcon checked={otherSelected} darkMode variant={isMultiple ? 'square' : 'circle'} />
              <p>其他（請填寫）</p>
            </Flexbox>
          )}
        </div>
        {otherSelected && (
          <div>
            <Input
              darkMode
              isValid={!isOtherInputError}
              {...register(`surveys.${index}.other`, {
                required: {
                  value: true,
                  message: '請填寫其他內容',
                },
                shouldUnregister: true,
              })}
            />
            <HookFormError errors={errors} name={`surveys.${index}.other`} />
          </div>
        )}
      </div>
    </FieldBlock>
  );
};
